import { useEffect } from "react";

// Store the active refs globally, so we only attach one event listener
const clickOutsideRefs = [];

function handleClickOutsideGlobal(event) {
    // Loop through all stored refs to check if any are clicked outside
    clickOutsideRefs.forEach(({ ref, callback, ignoreIds }) => {
        try {
            const id = event.target?.getAttribute('id');
            const parent = event.target?.parentElement;
            const id2 = parent?.getAttribute('id');
            const grandparent = event.target?.parentElement?.parentElement;
            const id3 = grandparent?.getAttribute('id');

            if (ref.current && !ref.current.contains(event.target)) {
                for (const str of ignoreIds) {
                    if ((id && id.includes(str)) || (id2 && id2.includes(str)) || (id3 && id3.includes(str))) {
                        return; // Don't call callback if the click is within excluded elements
                    }
                }
                setTimeout(callback, 0); // Call the callback function
            }
        } catch (e) {
            console.warn(e.message)
        }
    });
}

/**
 * This hook calls a function when the user clicks outside the element with the supplied ref. By default, it ignores the
 * certain ids so that the Library Select Component doesn't count as an outside click (since some if the select component
 * is rendered in the document.body). To remove these set ids, pass an empty array for the ignoreIds parameter.
 * @param {React.ref} ref The ref that defines what element counts as inside (where a click will not call the function)
 * @param callback {Function} The function that is called when the user clicks outside
 * @param {Array<string>} [ignoreIds = ['option', 'multi-val', 'custom-menu', 'multi-val-remove']] ignoreIds an array of string ids that will not trigger the clicked outside call.
 */
export default function useClickOutside(ref, callback, ignoreIds = ['option', 'multi-val', 'custom-menu', 'multi-val-remove']) {
    useEffect(() => {
        // Add the ref, callback, and ignoreIds to the global list of refs
        clickOutsideRefs.push({ ref, callback, ignoreIds });

        // Attach the global click event listener only once
        if (clickOutsideRefs.length === 1) {
            document.addEventListener("click", handleClickOutsideGlobal);
            document.addEventListener("drag", handleClickOutsideGlobal);
        }

        // Cleanup function
        return () => {
            // Remove the specific ref from the global list
            const index = clickOutsideRefs.findIndex(item => item.ref === ref);
            if (index !== -1) {
                clickOutsideRefs.splice(index, 1);
            }

            // Remove the global event listener if there are no more refs
            if (clickOutsideRefs.length === 0) {
                document.removeEventListener("click", handleClickOutsideGlobal);
                document.removeEventListener("drag", handleClickOutsideGlobal);
            }
        };
    }, [ref, callback, ignoreIds]); // Ensure the hook is re-run if ref, callback, or ignoreIds change
}